import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";

import {ButtonVariant} from "@/components/button/Button.types.ts";
import {Dropdown} from "@/components/dropdown/Dropdown.tsx";
import {NavTabs} from "@/components/navTabs/NavTabs.tsx";
import {TAB_ICONS, TAB_LABELS} from "@/lib/constants/subscribeDropdown";
import {useCopyToClipboard} from "@/lib/hooks/useCopyToClipboard";
import {useInheritedTheme} from "@/lib/hooks/useInheritedTheme.ts";
import {GQL_PUBLIC_STATUSPAGE_SUBSCRIBE} from "@/lib/queries/statusPageConsumption";
import {SubscriptionType} from "@/lib/types/statuspages";

import {schemaByType, SubscribeFormValues, SubscribeResult} from "./SubscribeDropdown.types";
import {SubscribeDropdownFooter} from "./SubscribeDropdownFooter";
import {TabContent} from "./TabContent";

import styles from "./SubscribeDropdown.module.scss";

interface SubscribeDropdownProps {
  slug: string;
  allowEmail: boolean;
  allowSms: boolean;
  allowRss: boolean;
  allowSlack: boolean;
  allowWebhook: boolean;
  rssUrl?: string | null;
  slackOAuthUrl?: string | null;
  variant?: ButtonVariant;
  usePortal?: boolean;
}

export const SubscribeDropdown = ({
  slug,
  allowEmail,
  allowSms,
  allowRss,
  allowSlack,
  allowWebhook,
  rssUrl,
  slackOAuthUrl,
  variant = "primary",
  usePortal = false,
}: SubscribeDropdownProps) => {
  const tabs: SubscriptionType[] = [
    ...(allowEmail ? ([SubscriptionType.EMAIL] as SubscriptionType[]) : []),
    ...(allowSlack ? ([SubscriptionType.SLACK] as SubscriptionType[]) : []),
    ...(allowRss ? ([SubscriptionType.RSS] as SubscriptionType[]) : []),
    ...(allowSms ? ([SubscriptionType.SMS] as SubscriptionType[]) : []),
    ...(allowWebhook ? ([SubscriptionType.WEBHOOK] as SubscriptionType[]) : []),
  ];

  const [activeType, setActiveType] = useState<SubscriptionType>(tabs[0] ?? SubscriptionType.EMAIL);
  const [serverError, setServerError] = useState<string | null>(null);
  const [successType, setSuccessType] = useState<SubscriptionType | null>(null);
  const [resolvedRssUrl, setResolvedRssUrl] = useState<string | null>(rssUrl ?? null);
  const [, copyToClipboard] = useCopyToClipboard();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const theme = useInheritedTheme(wrapperRef);

  const methods = useForm<SubscribeFormValues>({
    resolver: yupResolver(schemaByType[activeType]),
    defaultValues: {target: ""},
  });

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const [subscribe, {loading}] = useMutation<{publicStatusPageSubscribe: SubscribeResult}>(
    GQL_PUBLIC_STATUSPAGE_SUBSCRIBE
  );

  // Resolve RSS URL when RSS tab is active
  useEffect(() => {
    if (activeType !== SubscriptionType.RSS || resolvedRssUrl) return;
    subscribe({
      variables: {data: {slug, subscription_type: SubscriptionType.RSS, subscription_target: ""}},
    })
      .then((result) => {
        const response = result.data?.publicStatusPageSubscribe;
        if (response?.__typename === "PublicStatusPageSubscribeSuccess" && response.data.rss_url) {
          setResolvedRssUrl(response.data.rss_url);
        }
      })
      .catch(() => {});
  }, [activeType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (nextShow: boolean) => {
    if (!nextShow) {
      resetForm(tabs[0] ?? SubscriptionType.EMAIL);
    }
  };

  const resetForm = (type: SubscriptionType) => {
    methods.reset({target: ""});
    setServerError(null);
    setSuccessType(null);
    setActiveType(type);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
    setCopied(false);
  };

  const handleTabChange = (type: SubscriptionType) => {
    resetForm(type);
  };

  const handleCopyRss = async () => {
    if (!resolvedRssUrl) return;
    const success = await copyToClipboard(resolvedRssUrl);
    if (success) {
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }
  };

  const onSubmit = async ({target}: SubscribeFormValues) => {
    setServerError(null);

    if (activeType === SubscriptionType.RSS) {
      await handleCopyRss();
      return;
    }

    try {
      const result = await subscribe({
        variables: {
          data: {slug, subscription_type: activeType, subscription_target: target},
        },
      });

      const response = result.data?.publicStatusPageSubscribe;
      if (!response) {
        setServerError("An unexpected error occurred.");
        return;
      }

      if (response.__typename === "ErrorResponse") {
        const msg = response.message;
        if (msg && typeof msg === "object") {
          Object.entries(msg as Record<string, unknown>).forEach(([field, messages]) => {
            const rhfField = field === "__all__" ? "target" : field;
            const msgStr = Array.isArray(messages) ? messages.join(", ") : String(messages);
            methods.setError(rhfField as keyof SubscribeFormValues, {message: msgStr});
          });
        } else {
          setServerError(typeof msg === "string" ? msg : "An error occurred. Please try again.");
        }
        return;
      }

      if (response.__typename === "PublicStatusPageSubscribeSuccess") {
        if (response.data.rss_url) {
          setResolvedRssUrl(response.data.rss_url);
          setSuccessType(SubscriptionType.RSS);
        } else {
          setSuccessType(activeType);
        }
      }
    } catch {
      setServerError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <Dropdown ref={wrapperRef} onToggle={handleToggle} align="end">
      <Dropdown.Toggle variant={variant} size="small" className={styles.toggleWrapper}>
        Subscribe
      </Dropdown.Toggle>

      {/* The portaled panel escapes the themed page wrapper, so it has to carry data-theme itself. */}
      <Dropdown.Menu
        portal={usePortal}
        align="end"
        className={classNames(styles.dropdownPanel, {[styles.dropdownPanelPortal]: usePortal})}
        {...(theme ? {"data-theme": theme} : {})}
      >
        {/* Icon tabs */}
        {tabs.length > 1 && (
          <NavTabs
            className={styles.iconTabs}
            items={tabs.map((type) => {
              const Icon = TAB_ICONS[type];
              return {id: type, title: <Icon size={16} aria-label={TAB_LABELS[type]} />};
            })}
            selectedId={activeType}
            selectTab={(id) => handleTabChange(id as SubscriptionType)}
            variant="default"
          />
        )}

        {/* Tab content */}
        <TabContent
          successType={successType}
          activeType={activeType}
          serverError={serverError}
          slackOAuthUrl={slackOAuthUrl}
          methods={methods}
          onSubmit={onSubmit}
          resolvedRssUrl={resolvedRssUrl}
          loading={loading}
          copied={copied}
        />
        <Dropdown.Divider />
        <SubscribeDropdownFooter />
      </Dropdown.Menu>
    </Dropdown>
  );
};
